import {
  MIN_SCANNABLE_MODULE_DOTS,
  code128ModuleDots,
  isEncodableInCode128B,
} from '@sitzmark/core';

/**
 * What each label stock actually measures, and whether a code will scan on it
 * (SCOPE §7, §7.1; revision items 36 and 37).
 *
 * Pure, and separate from the component, because the interesting question here
 * is arithmetic rather than markup: **a barcode is drawn at whatever width the
 * label has, so the symbol always fits and the bars are what shrink.** Nothing
 * on screen and nothing on the paper says a label is unscannable — you find out
 * one item at a time at the register, weeks later, which is the exact failure
 * decision 29 was written to prevent one step earlier in the same pipeline.
 *
 * Sizes are configuration rather than code (§7.1), so a store picks what it owns
 * and a printer change is a settings edit.
 */

export type LabelSize = '2.25x1.25' | '57x32mm' | '2x1' | '1.5x1' | 'sheet-30up';

export interface LabelItem {
  readonly sku: string;
  readonly barcode: string | null;
  /** The style name. Line one, on its own — item 37. */
  readonly name: string;
  /** `size · colour`, the line beneath it. Empty on a single-variant product. */
  readonly detail: string;
  readonly quantity: number;
  readonly retailCents: number;
  readonly cardPriceCents?: number | null;
}

/**
 * What is actually encoded: the vendor's UPC when there is one, our SKU
 * otherwise.
 */
export function codeFor(item: LabelItem): string {
  return item.barcode ?? item.sku;
}

export interface LabelGeometry {
  /** As a person would say it, for the Settings picker and the run dialog. */
  readonly label: string;
  readonly widthIn: number;
  readonly heightIn: number;
  /**
   * Padding, each side, in inches: `[vertical, horizontal]`.
   *
   * Ink margin only. It used to be doing a second job — standing in for the
   * barcode's **quiet zone** — and it was bad at it, because a quiet zone is
   * specified in modules and this is a fixed number of inches. `Barcode` draws
   * the quiet zone inside its own viewBox now, which is what let the two
   * smallest stocks have some of their width back: at 0.08in of padding a 2 × 1
   * in label could not carry a 12-digit UPC and at 0.05in it can.
   */
  readonly paddingIn: readonly [vertical: number, horizontal: number];

  /**
   * Type size for this stock.
   *
   * **Set on every stock, including the big ones.** The 2.25 × 1.25in presets
   * inherited the application's 16px, which fitted a two-line name, a barcode
   * and a foot into a 1.25in label with two pixels to spare — and stopped
   * fitting the moment item 37 added the size-and-colour line, so a long name
   * printed *over* it. Found by looking at a printed label, not by any test;
   * a browser journey now asserts no label overflows its own box.
   */
  readonly fontSizePx: number;
}

/** Thermal label printers are 203 dpi (8 dots/mm); the shop's Zebra 2824 is one. */
export const LABEL_PRINTER_DPI = 203;

export const LABEL_SIZES: Readonly<Record<LabelSize, LabelGeometry>> = {
  /** Retail default (§7.1). Six thousandths of an inch wider than 57 × 32 mm. */
  '2.25x1.25': {
    label: '2.25 × 1.25 in',
    widthIn: 2.25,
    heightIn: 1.25,
    paddingIn: [0.08, 0.1],
    fontSizePx: 11,
  },
  /**
   * The stock this shop actually buys (item 36).
   *
   * 57 mm × 32 mm is 2.2441 × 1.2598 in, so `2.25x1.25` was within six
   * thousandths of an inch and worked. Named exactly anyway, because Chrome
   * scales or shifts the page when `@page` and the media disagree, and a shifted
   * barcode is an unscannable one.
   */
  '57x32mm': {
    label: '57 × 32 mm',
    widthIn: 2.2441,
    heightIn: 1.2598,
    paddingIn: [0.08, 0.1],
    fontSizePx: 11,
  },
  '2x1': { label: '2 × 1 in', widthIn: 2, heightIn: 1, paddingIn: [0.06, 0.05], fontSizePx: 9 },
  '1.5x1': {
    label: '1.5 × 1 in',
    widthIn: 1.5,
    heightIn: 1,
    paddingIn: [0.05, 0.04],
    fontSizePx: 8,
  },
  'sheet-30up': {
    label: 'Avery 30-up sheet',
    widthIn: 2.625,
    heightIn: 1,
    paddingIn: [0.06, 0.1],
    fontSizePx: 9,
  },
};

export const LABEL_SIZE_KEYS = Object.keys(LABEL_SIZES) as LabelSize[];

/**
 * `@page` is document-level and cannot be selected by class, so the right paper
 * is injected for the duration of the print. Without it a label roll gets laid
 * out on letter paper.
 *
 * Derived from the table above rather than written out beside it: two lists of
 * the same measurements is how the stylesheet and the printer come to disagree.
 */
export function pageRuleFor(size: LabelSize): string {
  const geometry = LABEL_SIZES[size];
  // Sheet stock is a page of labels, not a page per label.
  if (size === 'sheet-30up') return '@page { size: letter; margin: 0.5in 0.19in; }';
  return `@page { size: ${geometry.widthIn}in ${geometry.heightIn}in; margin: 0; }`;
}

/** The width the barcode is actually drawn across, once the padding is taken out. */
export function barcodeWidthIn(size: LabelSize): number {
  const geometry = LABEL_SIZES[size];
  return geometry.widthIn - 2 * geometry.paddingIn[1];
}

/**
 * The geometry as CSS custom properties, so the stylesheet reads the table
 * rather than repeating it.
 *
 * Decision 87's lesson, one layer down: a measurement written into a stylesheet
 * to describe something computed elsewhere is a guess that nothing will ever
 * check. Here the check that a code fits divides by these exact numbers, so a
 * stylesheet holding its own copy would let the arithmetic and the paper drift
 * apart without a single test going red.
 */
export function labelStyleFor(size: LabelSize): Record<string, string> {
  const geometry = LABEL_SIZES[size];
  return {
    '--label-width': `${geometry.widthIn}in`,
    '--label-height': `${geometry.heightIn}in`,
    '--label-padding': `${geometry.paddingIn[0]}in ${geometry.paddingIn[1]}in`,
    '--label-font-size': `${geometry.fontSizePx}px`,
  };
}

/**
 * How many printer dots wide each bar of this code would be on this stock.
 *
 * The figure rather than a verdict, so the run dialog can say "1.5 dots per bar"
 * — which tells somebody what to do — instead of "unscannable", which does not
 * (§2.2-10).
 */
export function moduleDotsFor(
  code: string,
  size: LabelSize,
  dpi: number = LABEL_PRINTER_DPI,
): number | null {
  if (!isEncodableInCode128B(code)) return null;
  return code128ModuleDots(code, barcodeWidthIn(size), dpi);
}

/**
 * The codes in a run that would not print scannably, worst first.
 *
 * Returned rather than refused. A shop that cannot print *any* label for an item
 * is worse off than one printing a marginal one, and this stock is 57 × 32 mm
 * where a six-digit SKU lands at four dots a bar with room to spare. What the
 * check is really for is the day somebody picks a smaller roll, or a code longer
 * than anything here arrives — and then it says so before four hundred labels
 * come off the printer rather than after.
 */
export interface UnscannableCode {
  readonly code: string;
  readonly moduleDots: number | null;
}

export function unscannableCodes(
  codes: readonly string[],
  size: LabelSize,
  dpi: number = LABEL_PRINTER_DPI,
): UnscannableCode[] {
  const seen = new Set<string>();

  return codes
    .filter((code) => (seen.has(code) ? false : (seen.add(code), true)))
    .map((code) => ({ code, moduleDots: moduleDotsFor(code, size, dpi) }))
    .filter((entry) => entry.moduleDots === null || entry.moduleDots < MIN_SCANNABLE_MODULE_DOTS)
    .sort((a, b) => (a.moduleDots ?? -1) - (b.moduleDots ?? -1));
}
